import { supabase } from '../auth/supabase-client';
import type { Database } from '../types/database.generated';
import type { Notification } from '../data/types';

function client() {
  if (!supabase) throw new Error('Supabase indisponible.');
  return supabase;
}

export type Reglages = Database['alba']['Tables']['member_settings']['Row'];

/** Ce que la cloche descend d'un coup : au delà, l'écran devient une archive. */
export const LIMITE_CLOCHE = 30;

export async function chargerNotifications(monMembreId: string): Promise<Notification[]> {
  const { data, error } = await client()
    .from('notifications')
    .select('*')
    .eq('member_id', monMembreId)
    .order('created_at', { ascending: false })
    .limit(LIMITE_CLOCHE);
  if (error) throw error;
  return data ?? [];
}

export async function compterNonLues(monMembreId: string): Promise<number> {
  const { count, error } = await client()
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('member_id', monMembreId)
    .is('read_at', null);
  if (error) throw error;
  return count ?? 0;
}

export async function marquerLue(id: string): Promise<void> {
  const { error } = await client()
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', id)
    .is('read_at', null);
  if (error) throw error;
}

export async function toutMarquerLu(monMembreId: string): Promise<void> {
  const { error } = await client()
    .from('notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('member_id', monMembreId)
    .is('read_at', null);
  if (error) throw error;
}

// --- Réglages ------------------------------------------------------------------

export async function chargerReglages(monMembreId: string): Promise<Reglages | null> {
  const { data, error } = await client()
    .from('member_settings')
    .select('*')
    .eq('member_id', monMembreId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export type ChangementReglages = Partial<
  Pick<
    Reglages,
    'push_enabled' | 'email_matin' | 'email_soir' | 'email_immediat' | 'quiet_from' | 'quiet_to'
  >
>;

export async function enregistrerReglages(
  monMembreId: string,
  changement: ChangementReglages,
): Promise<Reglages> {
  const { data, error } = await client()
    .from('member_settings')
    .upsert({ member_id: monMembreId, ...changement }, { onConflict: 'member_id' })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

// --- Message tout prêt ----------------------------------------------------------

/** Envoie au demandeur le message écrit au moment de la décision. */
export async function prevenirLeDemandeur(tacheId: string, message: string): Promise<boolean> {
  const { data, error } = (await client().functions.invoke('alba-notifications', {
    body: { action: 'prevenir', task_id: tacheId, message },
  })) as { data: { notification_id?: string; etat?: string } | null; error: unknown };
  if (error) throw new Error(await messageErreur(error, 'Le message n’est pas parti.'));
  return Boolean(data?.notification_id);
}

/** Demande à recevoir tout de suite son propre récap, pour vérifier son réglage. */
export async function envoyerMonRecap(moment: 'matin' | 'soir'): Promise<string> {
  const { data, error } = (await client().functions.invoke('alba-notifications', {
    body: { action: 'recap', moment },
  })) as { data: { resultats?: { etat?: string }[] } | null; error: unknown };
  if (error) throw new Error(await messageErreur(error, 'Le récap n’est pas parti.'));
  return data?.resultats?.[0]?.etat ?? 'INCONNU';
}

async function messageErreur(erreur: unknown, defaut: string): Promise<string> {
  const contexte = (erreur as { context?: Response }).context;
  if (contexte && typeof contexte.json === 'function') {
    try {
      const charge = (await contexte.json()) as { error?: string };
      if (charge.error) return charge.error;
    } catch {
      // Réponse sans JSON : on garde le message générique.
    }
  }
  return (erreur as Error).message || defaut;
}
